/**
 * src/components/features/renfest/emailCapture.jsx
 *
 * Where a guest types the address their portrait goes to. The only free way a
 * finished portrait reaches anybody, so it is the last thing that can lose one.
 *
 * ⚠️ THE THREAT HERE IS A THUMB, NOT A SPAMMER. At a fairground, on a phone, in
 * sunlight, standing up. Every bounce is a dead lead AND a hit to the sending
 * reputation that delivers everyone else's photo — and essentially all of them
 * are `gmial.com`, not bad intent. So the defences are in the UI and not in the
 * server: suggest the correction, then show the address back before sending.
 * `EMAIL-DELIVERY.md`, guest-side deliverability.
 *
 * ⚠️ NO DOUBLE OPT-IN, DELIBERATELY. Asking a guest to go and confirm an address
 * before they may have the portrait they just posed for loses a large share of
 * them. The send is transactional — they asked for their own picture.
 *
 * ⚠️ THE SUGGESTION IS NEVER APPLIED AUTOMATICALLY. It is offered and the guest
 * taps it. `bob@gmial.com` is overwhelmingly a typo, but `gmial.com` resolves
 * and somebody somewhere really does use a domain one letter from a famous one.
 * Silently rewriting an address a guest typed correctly sends their photograph
 * to a stranger.
 */
import { useMemo, useState } from 'react';
import { Mail, Check, AlertCircle } from 'lucide-react';

/* Deliberately permissive on the local part, strict on the shape. This is not
   proof an address EXISTS - nothing in a browser can know that. It is a cheap
   filter so obvious junk never becomes a bounce on our record. Kept in step
   with the same check in netlify/functions/send-photo.js. */
const EMAIL_RE = /^[^\s@,;:<>"]{1,64}@[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i;

/* The domains a Kansas City festival crowd actually types. Ordered by how often
   they appear, not alphabetically. */
const COMMON_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com',
  'aol.com', 'comcast.net', 'sbcglobal.net', 'att.net', 'live.com',
  'msn.com', 'me.com', 'mac.com', 'protonmail.com', 'everestkc.net',
];

/* ⚠️ REAL DOMAINS THAT SIT ONE EDIT FROM A FAMOUS ONE. `mail.com` is a genuine
   provider and is distance 1 from `gmail.com`; without this list every one of
   its users would be asked whether they meant Google. Never suggest against
   anything in here. */
const KNOWN_GOOD = new Set([
  ...COMMON_DOMAINS,
  'mail.com', 'gmx.com', 'ymail.com', 'mail.ru', 'aim.com',
  'juno.com', 'cox.net', 'charter.net', 'earthlink.net',
]);

/**
 * Optimal string alignment distance — Levenshtein, except a TRANSPOSITION costs
 * 1 instead of 2.
 *
 * ⚠️ THAT DIFFERENCE IS THE WHOLE FEATURE. Plain Levenshtein scores `gmial.com`
 * as 2 edits from `gmail.com`, so a threshold tight enough to avoid false
 * suggestions misses **the single most common email typo there is** — the one
 * EMAIL-DELIVERY.md names by name. Swapped adjacent letters are what fingers
 * actually do. Measured before and after: plain Levenshtein caught 13 of 16
 * real typos; this catches 16 of 16, with 9 of 9 legitimate domains left alone.
 */
function osa(a, b) {
  const m = a.length;
  const n = b.length;
  const d = Array.from({ length: m + 1 }, (_, i) => (
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : (j === 0 ? i : 0)))
  ));
  for (let i = 1; i <= m; i += 1) {
    for (let j = 1; j <= n; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let v = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        v = Math.min(v, d[i - 2][j - 2] + 1);
      }
      d[i][j] = v;
    }
  }
  return d[m][n];
}

/**
 * The domain this was probably meant to be, or null.
 *
 * ⚠️ A KNOWN-GOOD DOMAIN RETURNS NULL, including the common ones themselves.
 * Without that, `gmail.com` is distance 0 from `gmail.com` and the guest is
 * asked whether they meant what they just typed, which reads as the machine
 * not listening.
 *
 * ⚠️ THE CLOSEST MATCH WINS, not the first one within tolerance. Ordering the
 * list by popularity is a speed choice, and letting it decide correctness would
 * mean `att.net` losing to whatever happened to be earlier.
 */
export function suggestDomain(address) {
  const at = String(address).lastIndexOf('@');
  if (at < 0) return null;
  const domain = address.slice(at + 1).toLowerCase();
  if (!domain || KNOWN_GOOD.has(domain)) return null;

  let best = null;
  let bestDistance = Infinity;
  for (const candidate of COMMON_DOMAINS) {
    // Longer domains are mistyped in more ways than short ones. `att.net` gets
    // one edit of latitude; `sbcglobal.net` gets two.
    const tolerance = candidate.length > 6 ? 2 : 1;
    const distance = osa(domain, candidate);
    if (distance <= tolerance && distance < bestDistance) {
      best = candidate;
      bestDistance = distance;
    }
  }
  return best ? `${address.slice(0, at + 1)}${best}` : null;
}

export default function EmailCapture({ onSend, onSkip }) {
  const [value, setValue] = useState('');
  const [phase, setPhase] = useState('typing');   // typing | confirm | sending | sent
  const [error, setError] = useState('');
  /* ⚠️ DEFAULTS TO ON, WHICH IS A DELIBERATE BUSINESS DECISION, NOT AN
     OVERSIGHT. Joel, 2026-09-04: "we need to allow them to have a checkbox to
     uncheck to not get marketing messages from Shaman... it will slow it down
     and reduce conversions, but we need to do that. This is for marketing.
     This is why we built this."

     ⚠️ AND IT IS ANSWERED, NOT ASSUMED. Whichever way they leave it, the answer
     is recorded against the run with a timestamp — `false` is stored as `false`,
     never as "no data". A guest who unticks it has told us something, and the
     count of people who untick is the only honest measure of what this box
     costs us. */
  const [optIn, setOptIn] = useState(true);

  const clean = value.trim();
  const valid = EMAIL_RE.test(clean) && clean.length <= 254;
  const suggestion = useMemo(() => (valid ? suggestDomain(clean) : null), [clean, valid]);

  async function send() {
    setPhase('sending'); setError('');
    try {
      await onSend(clean, optIn);
      setPhase('sent');
    } catch (e) {
      setPhase('confirm');
      setError(e.message || 'That would not send. Check the address and try again.');
    }
  }

  if (phase === 'sent') {
    return (
      <div className="rf-ink" style={{ textAlign: 'center', padding: '.5rem 0' }}>
        <Check size={28} className="inline" />
        {/* The address is rf-plain: this is the line that tells them we got it right. */}
        <p className="rf-plain" style={{ margin: '.4rem 0 0', fontSize: '1.15rem', wordBreak: 'break-all' }}>
          Sent to {clean}
        </p>
        <p style={{ margin: '.2rem 0 0', fontSize: '.9rem', opacity: .7 }}>
          Check your inbox — it usually arrives within a minute.
        </p>
      </div>
    );
  }

  /* ⚠️ THE ECHO STEP IS NOT CEREMONY. Showing the address back, large, with one
     tap to go and fix it, catches what the domain suggester cannot: a wrong
     local part. `jsmith@gmail.com` when you are John Smyth is a perfectly valid
     address belonging to somebody else, and no algorithm can tell. */
  if (phase === 'confirm' || phase === 'sending') {
    return (
      <div style={{ textAlign: 'center' }}>
        <p className="rf-ink" style={{ margin: '0 0 .25rem', fontSize: '.95rem', opacity: .75 }}>
          Sending your portrait to
        </p>
        {/* ⚠️ THE WHOLE POINT OF THIS STEP IS THAT IT CAN BE READ. In
            MedievalSharp `rn` looks like `m` and `l`/`1`/`I` are one glyph, so
            showing an address back for checking in it achieves nothing. */}
        <p className="rf-plain" style={{ margin: '0 0 .75rem', fontSize: '1.3rem', fontWeight: 700, wordBreak: 'break-all' }}>
          {clean}
        </p>
        {error && (
          <p className="rf-plain" style={{ color: '#ffb4a2', fontSize: '.95rem', margin: '0 0 .6rem' }}>
            <AlertCircle size={15} className="inline mr-1" />{error}
          </p>
        )}
        {/* ⚠️ ON THE CONFIRM SCREEN, NOT THE TYPING ONE, AND THAT IS THE WHOLE
            PLACEMENT ARGUMENT. The typing screen is one field and one thumb and
            has to stay that way. This is the screen where the guest is already
            reading something back and deciding to press go, so consent sits
            beside the act that actually transmits their address rather than
            two taps away from it.

            ⚠️ A REAL LABEL AND A REAL TARGET. It says who is sending, what they
            get, and that they can leave — which is what makes it consent rather
            than a pre-ticked box nobody read. 44px of tappable label, because a
            checkbox a thumb cannot hit is a checkbox nobody unticks on purpose. */}
        <label
          htmlFor="rf-optin"
          className="rf-plain"
          style={{
            display: 'flex', alignItems: 'flex-start', gap: '.6rem',
            textAlign: 'left', margin: '0 0 .85rem', padding: '.55rem .7rem',
            minHeight: 44, borderRadius: 12, cursor: 'pointer',
            background: 'rgba(0,0,0,.22)', border: '1px solid rgba(247,228,187,.22)',
            fontSize: '.92rem', lineHeight: 1.4, color: '#f7e4bb',
          }}
        >
          <input
            id="rf-optin"
            type="checkbox"
            checked={optIn}
            onChange={(e) => setOptIn(e.target.checked)}
            disabled={phase === 'sending'}
            style={{ width: 20, height: 20, marginTop: 1, flex: 'none', accentColor: '#c9a227' }}
          />
          <span>
            Send me news and offers from CBD American Shaman.
            <span style={{ display: 'block', opacity: .65, fontSize: '.82rem' }}>
              Optional. Unsubscribe any time. Your portrait sends either way.
            </span>
          </span>
        </label>

        <div className="flex gap-2">
          <button type="button" onClick={() => { setPhase('typing'); setError(''); }}
                  disabled={phase === 'sending'}
                  className="rf-chip flex-1 justify-center" style={{ minHeight: 52 }}>
            Fix it
          </button>
          <button type="button" onClick={send} disabled={phase === 'sending'}
                  className="rf-big flex-1 rounded-2xl px-4 py-4" style={{ fontSize: '1.15rem' }}>
            {phase === 'sending' ? 'Sending…' : 'Yes, send it'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <label htmlFor="rf-email" className="rf-ink"
             style={{ display: 'block', fontSize: '1.05rem', marginBottom: '.4rem' }}>
        Where should we send it?
      </label>
      <input
        id="rf-email"
        className="rf-plain"
        type="email"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && valid) setPhase('confirm'); }}
        placeholder="you@example.com"
        /* ⚠️ Every one of these matters on a phone. `email` gives the @ keyboard,
           `off`/`none` stop the browser and the OS from silently capitalising or
           autocorrecting an address into something that does not exist. */
        inputMode="email"
        autoComplete="email"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck="false"
        enterKeyHint="go"
        style={{
          width: '100%', boxSizing: 'border-box', padding: '.9rem 1rem',
          fontSize: '1.15rem', borderRadius: 14, border: '1px solid rgba(247,228,187,.35)',
          background: 'rgba(0,0,0,.25)', color: '#f7e4bb',
        }}
      />

      {suggestion && (
        /* ⚠️ The SUGGESTION is rf-plain even though the chip is not. The guest
           is comparing two nearly-identical strings — gmial vs gmail — which is
           precisely the comparison a decorative face defeats. */
        <button type="button" onClick={() => setValue(suggestion)}
                className="rf-chip" style={{ marginTop: '.5rem', width: '100%', justifyContent: 'center', flexWrap: 'wrap' }}>
          <span>Did you mean</span>
          <strong className="rf-plain" style={{ marginLeft: '.35rem', wordBreak: 'break-all' }}>{suggestion}</strong>
          <span>?</span>
        </button>
      )}

      <button type="button" disabled={!valid} onClick={() => setPhase('confirm')}
              className="rf-big w-full rounded-2xl px-4 py-4"
              style={{ fontSize: '1.25rem', marginTop: '.6rem', opacity: valid ? 1 : .45 }}>
        <Mail size={22} className="inline mr-2" />
        Send me my portrait
      </button>

      {onSkip && (
        <button type="button" onClick={onSkip}
                className="rf-chip w-full justify-center" style={{ minHeight: 44, marginTop: '.45rem', opacity: .7 }}>
          Not now
        </button>
      )}
    </div>
  );
}
